/**
 * Session Management Hooks
 *
 * These hooks provide functionality for managing authentication sessions,
 * JWT tokens, and session-related operations.
 */
import { useAuthContext } from './core-hooks';
/**
 * Hook to get JWT token
 */
export function useJWT() {
    const { jwt } = useAuthContext();
    return jwt;
}
/**
 * Hook to get session data
 */
export function useSessionData() {
    const { session } = useAuthContext();
    return session;
}
/**
 * Hook to refresh authentication
 * Note: useRefreshAuth is exported from session-actions.ts for enhanced functionality
 */
/**
 * Hook to logout
 * Note: useLogout is exported from session-actions.ts for enhanced functionality
 */
/**
 * Hook to check session
 */
export function useCheckSession() {
    const { checkSession } = useAuthContext();
    return checkSession;
}
/**
 * Hook to update auth state
 */
export function useUpdateAuthState() {
    const { updateAuthState } = useAuthContext();
    return updateAuthState;
}
/**
 * Hook to clear auth state
 */
export function useClearAuthState() {
    const { clearAuthState } = useAuthContext();
    return clearAuthState;
}
/**
 * Hook to set auth error
 */
export function useSetAuthError() {
    const { setAuthError } = useAuthContext();
    return setAuthError;
}
/**
 * Custom hook for session management (wrapper around auth context)
 * Provides a convenient interface for session-related operations
 */
export function useSession() {
    const { session, jwt, isLoading, isAuthenticated, refreshAuth, logout } = useAuthContext();
    return {
        session,
        jwt,
        isLoading,
        isAuthenticated,
        refreshSession: refreshAuth,
        logout,
    };
}
/**
 * Hook to refresh the current session
 * Returns a function that refreshes session data from the auth provider
 */
export function useRefreshSession() {
    const { refreshAuth } = useAuthContext();
    return refreshAuth;
}
